import type { GlossaryEntry } from './types';

const entries: GlossaryEntry[] = [
  // ── Loop vocabulary ───────────────────────────────────────────────
  {
    id: 'plant',
    label: 'plant',
    meaning:
      'The physical thing being controlled. In robotics this could be the vehicle, drone airframe, or mechanism whose motion is being guided by a flight controller. In this course it is the 1 kg multirotor.',
    example: 'Our 1 kg multirotor moving along a vertical guide rail, including its motor mass, air drag, and optional tether spring.',
  },
  {
    id: 'actuator',
    label: 'actuator',
    meaning:
      'The physical hardware that converts a flight controller command into an active force or torque on the real world. In multirotors, electric motors and spinning propellers act as actuators to generate upward aerodynamic thrust.',
    example: 'The drone rotors produce upward thrust between 0 N (idle) and $T_{max} = 25\\text{ N}$ (full throttle).',
    highlight: 'force:thrust',
  },
  {
    id: 'sensor',
    label: 'sensor',
    meaning:
      'The measurement device that observes what the vehicle is actually doing in the physical world, enabling closed-loop feedback. Here: an altimeter that reports height above the ground.',
    example: 'The altimeter reports $y_m = 2.51\\text{ m}$ when true altitude is 2.50 m and sensor noise is +0.01 m.',
    highlight: 'signal:ym',
  },
  {
    id: 'reference',
    label: 'reference (target)',
    tex: 'r',
    meaning:
      'The target altitude setpoint that the flight controller is commanded to reach and hold, displayed on the altitude plot as a dashed horizontal line.',
    units: 'm',
    example: 'A step command ordering the flight controller to climb from 2.0 m to 2.5 m at $t = 1\\text{ s}$.',
    highlight: 'signal:r',
    spoken: 'r',
  },
  {
    id: 'disturbance',
    label: 'disturbance',
    tex: 'd',
    meaning:
      'An unplanned external environmental force acting on the aircraft that the flight controller did not request (positive upward, negative downward). A robust flight controller must detect and counteract disturbances.',
    units: 'N (newtons)',
    example: 'A sudden downward wind gust or a hanging 1 N package payload ($d = -1\\text{ N}$).',
    highlight: 'force:disturbance',
    spoken: 'd',
  },
  {
    id: 'error',
    label: 'error',
    tex: 'e',
    meaning:
      'The instantaneous gap between target reference and measured position: target minus measurement ($e = r - y_m$). Positive error means the drone is below target and must climb; negative error means it is too high.',
    units: 'm',
    example: 'Target is 2.5 m, measured altitude is 2.3 m → error $e = 2.5 - 2.3 = +0.2\\text{ m}$ (too low).',
    highlight: 'signal:e',
    spoken: 'e',
  },
  {
    id: 'edot',
    label: 'derivative of error',
    tex: '\\dot{e}',
    meaning:
      'The instantaneous rate of change of tracking error over time ($\\dot e = \\dot r - \\dot y_m$). In textbook control, multiplying $\\dot e$ by $K_d$ provides derivative action, but direct differentiation causes derivative kick when the setpoint steps.',
    units: 'm/s',
    example: 'A sudden step command in target altitude causes error to jump instantly, making the rate of change of error $\\dot e$ momentarily infinite.',
    highlight: 'signal:e',
    spoken: 'e dot',
  },
  {
    id: 'feedback',
    label: 'feedback',
    meaning:
      'A reactive control strategy where the flight controller compares sensor measurements against the target in real time and commands corrections. Feedback is the only mechanism that can fix unmodeled forces and unexpected surprises.',
    example: 'A downdraft sags the drone below target; the altimeter detects the drop, and feedback automatically increases motor thrust to recover.',
  },
  {
    id: 'feedforward',
    label: 'feedforward',
    tex: 'T_{ff}',
    meaning:
      'A proactive baseline command calculated directly from a mathematical model before errors occur. It supplies the anticipated effort needed to fly, without waiting for the aircraft to fall first.',
    units: 'N',
    example: 'Predicting in advance that hovering our 1 kg drone requires a baseline thrust of $T_0 = m\\,g = 9.81\\text{ N}$.',
    highlight: 'signal:Tff',
    spoken: 'T f f',
  },
  {
    id: 'ufb',
    label: 'feedback correction',
    tex: 'u_{fb}',
    meaning:
      'The active corrective thrust adjustment added to (or subtracted from) the baseline feedforward command by the feedback controller to eliminate tracking error.',
    units: 'N',
    example: 'When $0.25\\text{ m}$ below target with $K_p = 4\\text{ N/m}$, the feedback controller requests $u_{fb} = +1\\text{ N}$ of extra upward thrust.',
    spoken: 'u f b',
  },
  {
    id: 'controller',
    label: 'controller',
    meaning:
      'The software algorithm running on the flight computer that processes sensor readings, target setpoints, and internal models to decide motor thrust commands.',
    example: 'A proportional control rule that adds 4 N of corrective thrust for every metre the vehicle sits below target.',
  },
  {
    id: 'open-loop',
    label: 'open loop',
    meaning:
      'Controlling actuators purely based on pre-planned commands or internal models without checking sensor measurements. If conditions differ from the model, open-loop control cannot detect or correct the error.',
    example: 'Commanding thrust to 9.81 N and assuming the drone will hover, without ever looking at the altimeter reading.',
  },
  {
    id: 'closed-loop',
    label: 'closed loop',
    meaning:
      'A complete feedback loop where sensor measurements continuously inform control decisions: plant → sensor → controller → actuator → plant.',
    example: 'A flight controller reading the altimeter 200 times per second and continuously adjusting rotor thrust to hold 2.5 m.',
  },

  // ── Time and motion ───────────────────────────────────────────────
  {
    id: 't',
    label: 'time',
    tex: 't',
    meaning: 'Elapsed flight time in seconds from the start of the simulation run.',
    units: 's (seconds)',
    example: 'At $t = 1\\text{ s}$, the flight controller initiates a step climb from 2.0 m to 2.5 m.',
    spoken: 't',
  },
  {
    id: 'dt',
    label: 'time step',
    tex: '\\Delta t',
    meaning:
      'The discrete sampling interval of the flight software loop: how much time passes between successive sensor readings and motor updates. $\\Delta$ (Greek delta) denotes an incremental change.',
    units: 's',
    example: '$\\Delta t = 0.005\\text{ s}$ (a 200 Hz update loop), executing 200 simulation cycles per second.',
    spoken: 'delta t',
  },
  {
    id: 'z',
    label: 'altitude',
    tex: 'z',
    meaning: 'The vertical position of the vehicle above the ground reference plane. By convention, upward is positive.',
    units: 'm (metres)',
    example: '$z = 2.0\\text{ m}$ is the nominal hover operating altitude in our simulation presets.',
    highlight: 'signal:z',
    spoken: 'z',
  },
  {
    id: 'v',
    label: 'velocity',
    tex: 'v',
    meaning:
      'Vertical velocity: the instantaneous rate of change of altitude over time ($v = \\dot z$). Positive velocity means climbing upward; negative velocity means descending toward the ground.',
    units: 'm/s',
    example: '$v = -0.5\\text{ m/s}$ means descending toward the ground at half a metre per second.',
    highlight: 'signal:v',
    spoken: 'v',
  },
  {
    id: 'a',
    label: 'acceleration',
    tex: 'a',
    meaning:
      'Vertical acceleration: the rate of change of velocity over time ($a = \\dot v$). Acceleration always points in the direction of the net physical force ($a = F_{net}/m$), not necessarily the direction of motion.',
    units: 'm/s²',
    example: 'Climbing upward ($v > 0$) with thrust below weight produces negative acceleration ($a < 0$), causing the climb to slow down.',
    highlight: 'signal:a',
    spoken: 'a',
  },
  {
    id: 'dot',
    label: 'dot notation',
    tex: '\\dot{z}',
    meaning:
      'In control notation, a dot above a quantity means its rate of change with time (its time derivative, $d/dt$). Two dots denote a second derivative (acceleration).',
    example: '$\\dot z = v$ (altitude rate is velocity); $\\ddot z = \\dot v = a$ (velocity rate is acceleration).',
    spoken: 'z dot',
  },
  {
    id: 'derivative',
    label: 'derivative (rate)',
    meaning:
      'The instantaneous rate at which a variable changes with respect to time: change in value divided by the elapsed time slice. Flight controllers track derivatives to anticipate where the aircraft is heading.',
    example: 'Altitude climbing from 2.00 m to 2.01 m in 0.01 s yields a velocity rate of $\\approx 1\\text{ m/s}$.',
  },
  {
    id: 'integral',
    label: 'integral (accumulation)',
    meaning:
      'The accumulated sum of a quantity multiplied by time. Geometrically, this is the signed area under a time-series plot. Flight controllers integrate error to build an ongoing memory of persistent bias.',
    example: 'Holding an altitude error of 0.1 m for 3 seconds accumulates an error integral of $0.1 \\times 3 = 0.3\\text{ m}\\cdot\\text{s}$.',
  },

  // ── Physical parameters ───────────────────────────────────────────
  {
    id: 'm',
    label: 'mass',
    tex: 'm',
    meaning:
      'Vehicle mass: the total quantity of matter in the aircraft, measuring its physical inertia (resistance to acceleration when acted upon by a net force).',
    units: 'kg',
    example: '$m = 1\\text{ kg}$ in our multirotor presets.',
    spoken: 'm',
  },
  {
    id: 'g',
    label: 'gravity',
    tex: 'g',
    meaning:
      'Gravitational acceleration at the Earth’s surface: the downward acceleration imparted to all free-falling bodies by Earth’s gravity.',
    units: 'm/s²',
    example: '$g = 9.81\\text{ m/s}^2$, meaning gravity pulls on our 1 kg drone with $9.81\\text{ N}$ of downward force.',
    highlight: 'force:gravity',
    spoken: 'g',
  },
  {
    id: 'weight',
    label: 'weight',
    tex: 'mg',
    meaning: 'The downward gravitational pull exerted on the vehicle: mass multiplied by gravitational acceleration ($m \\times g$).',
    units: 'N',
    example: '$1\\text{ kg} \\times 9.81\\text{ m/s}^2 = 9.81\\text{ N}$ pulling straight down.',
    highlight: 'force:gravity',
    spoken: 'm g',
  },
  {
    id: 'T',
    label: 'applied thrust',
    tex: 'T',
    meaning:
      'The actual physical upward aerodynamic force delivered by the spinning rotors to the airframe, after motor speed limits and battery boundaries are applied.',
    units: 'N',
    example: 'Commanding 30 N when motors max out at 25 N delivers an applied thrust of $T = 25\\text{ N}$.',
    highlight: 'signal:T',
    spoken: 'T',
  },
  {
    id: 'Tcmd',
    label: 'requested thrust',
    tex: 'T_{cmd}',
    meaning:
      'The raw thrust value calculated and requested by the flight controller. If this request exceeds motor limits or asks for negative thrust, the actuator hardware clamps it.',
    units: 'N',
    example: 'A controller requests $T_{cmd} = -3\\text{ N}$, but because propellers cannot pull downward, applied thrust clamps to $T = 0\\text{ N}$.',
    highlight: 'signal:Tcmd',
    spoken: 'T command',
  },
  {
    id: 'Tmax',
    label: 'maximum thrust',
    tex: 'T_{max}',
    meaning: 'The maximum total upward thrust that the propulsion system (rotors, motors, electronic speed controllers, battery) can physically produce.',
    units: 'N',
    example: '$T_{max} = 25\\text{ N}$, providing a 2.5:1 thrust-to-weight ratio for aggressive climb authority.',
    spoken: 'T max',
  },
  {
    id: 'air-drag',
    label: 'air drag',
    meaning:
      'Aerodynamic resistance exerted by the surrounding air, modeled here as linear viscous damping ($-b\\,v$). It always acts in the opposite direction of vehicle velocity.',
    units: 'N',
    example: 'Climbing at 1.19 m/s through air with damping $b = 1\\text{ N}\\cdot\\text{s/m}$ creates 1.19 N of downward drag force.',
    highlight: 'force:damping',
  },
  {
    id: 'b',
    label: 'damping coefficient',
    tex: 'b',
    meaning:
      'Physical aerodynamic damping coefficient: the magnitude of drag force produced per unit of vertical velocity. Damping force is $-b\\,v$.',
    units: 'N·s/m',
    example: 'With $b = 1\\text{ N}\\cdot\\text{s/m}$, ascending at $v = +2\\text{ m/s}$ creates $2\\text{ N}$ of downward drag.',
    highlight: 'force:damping',
    spoken: 'b',
  },
  {
    id: 'k',
    label: 'spring stiffness',
    tex: 'k',
    meaning:
      'Physical stiffness of the tether spring on our training rig: the restoring force generated per metre of tether extension. (This is a physical mechanical property of the rig, not a flight controller gain).',
    units: 'N/m',
    example: '$k = 2\\text{ N/m}$: lifting the drone 0.5 m above rest stretches the spring and pulls it downward with $1\\text{ N}$ of force.',
    highlight: 'force:spring',
    spoken: 'k',
  },
  {
    id: 'zs',
    label: 'spring rest altitude',
    tex: 'z_s',
    meaning:
      'The neutral resting altitude of the teaching tether where the spring is neither stretched nor compressed, exerting zero physical force.',
    units: 'm',
    example: '$z_s = 2.0\\text{ m}$: at $z = 2.5\\text{ m}$ the spring pulls downward; at $z = 1.5\\text{ m}$ it pushes upward.',
    highlight: 'scene:spring',
    spoken: 'z s',
  },
  {
    id: 'spring',
    label: 'teaching spring',
    meaning:
      'An elastic tether attached to the aircraft in our training rig to provide natural mechanical centering for initial experiments. Real free-flying multirotors have no tether ($k = 0$).',
    example: 'The tethered preset uses stiffness $k = 2\\text{ N/m}$; the free-altitude preset sets $k = 0\\text{ N/m}$.',
    highlight: 'scene:spring',
  },
  {
    id: 'force',
    label: 'force',
    meaning:
      'A physical push or pull acting upon an object, measured in Newtons (N). One Newton is the force needed to accelerate a 1 kg mass at 1 m/s².',
    units: 'N',
    example: 'Holding a 100-gram smartphone stationary against gravity requires approximately 1 N of upward supporting force.',
  },
  {
    id: 'net-force',
    label: 'net force',
    meaning:
      'The combined vector sum of all individual forces acting on the aircraft along the vertical axis (taking upward as positive). Net force dictates instantaneous acceleration ($F_{net} = m\\,a$).',
    units: 'N',
    example: 'Upward thrust of 12 N minus weight of 9.81 N produces a net upward force of $+2.19\\text{ N}$, causing the drone to accelerate upward.',
    highlight: 'force:net',
  },

  // ── Operating point and state space ───────────────────────────────
  {
    id: 'equilibrium',
    label: 'equilibrium',
    meaning:
      'A steady flight condition where all opposing forces perfectly balance out, leaving zero net force and zero acceleration so the vehicle remains stationary in hover.',
    example: 'Hovering motionlessly at 2.0 m with rotor thrust exactly equal to vehicle weight ($T = m\\,g$).',
  },
  {
    id: 'z0',
    label: 'operating altitude',
    tex: 'z_0',
    meaning:
      'The designated equilibrium hover altitude around which the flight controller equations and deviations are linearized. Subscript 0 designates the nominal operating baseline.',
    units: 'm',
    example: '$z_0 = 2.0\\text{ m}$ in all standard simulation presets.',
    spoken: 'z zero',
  },
  {
    id: 'T0',
    label: 'equilibrium thrust',
    tex: 'T_0',
    meaning:
      'The exact rotor thrust required to balance gravity and resting tether tension to hover motionless at $z_0$ with no disturbance: $T_0 = m\\,g + k(z_0 - z_s)$.',
    units: 'N',
    example: 'For our 1 kg drone hovering at $z_0 = z_s = 2\\text{ m}$, equilibrium thrust is $T_0 = 1 \\times 9.81 = 9.81\\text{ N}$.',
    spoken: 'T zero',
  },
  {
    id: 'q',
    label: 'altitude deviation',
    tex: 'q',
    meaning:
      'The aircraft’s current altitude measured as a displacement relative to the operating hover point ($q = z - z_0$). Positive $q$ means flying above nominal hover height.',
    units: 'm',
    example: 'At true altitude $z = 2.3\\text{ m}$ with $z_0 = 2.0\\text{ m}$, the altitude deviation is $q = +0.3\\text{ m}$.',
    highlight: 'signal:z',
    spoken: 'q',
  },
  {
    id: 'u',
    label: 'thrust deviation',
    tex: 'u',
    meaning:
      'The amount of motor thrust commanded above or below baseline hover thrust ($u = T - T_0$). This represents the control input in linearized equations.',
    units: 'N',
    example: 'Applying 11.0 N when hover thrust is 9.81 N gives a thrust deviation of $u = +1.19\\text{ N}$.',
    highlight: 'signal:T',
    spoken: 'u',
  },
  {
    id: 'state',
    label: 'state',
    tex: 'x',
    meaning:
      'The minimal set of physical variables that, along with future control inputs, completely dictates the future trajectory of the aircraft. For our 1D drone, the state vector is position deviation $q$ and velocity $v$.',
    example: 'State vector $x = [q, v]^T = [0.3\\text{ m}, -0.1\\text{ m/s}]^T$.',
    spoken: 'x',
  },
  {
    id: 'xdot',
    label: 'state rate',
    tex: '\\dot{x}',
    meaning:
      'The time derivative of the state vector: the instantaneous rates of change of each state variable, stacked in matching order: $\\dot x = [\\dot q, \\dot v]^T = [v, a]^T$.',
    example: '$\\dot x = [v, a]^T = [-0.1\\text{ m/s}, 0.4\\text{ m/s}^2]^T$.',
    spoken: 'x dot',
  },
  {
    id: 'scalar',
    label: 'scalar',
    meaning: 'A single numerical quantity (along with its units), in contrast to a multi-element vector or matrix table.',
    example: 'Vehicle mass $m = 1\\text{ kg}$ and hover thrust $T_0 = 9.81\\text{ N}$ are scalars.',
  },
  {
    id: 'vector',
    label: 'vector',
    meaning:
      'An ordered column or list of numbers representing multi-variable states, forces, or commands. In state-space control, the index order is strictly preserved.',
    example: 'The state vector $x = [q, v]^T$ always places altitude deviation first and velocity second.',
  },
  {
    id: 'matrix',
    label: 'matrix',
    meaning:
      'A rectangular grid of numerical coefficients arranged in rows and columns. In control systems, matrices compactly encode physical equations of motion, cross-couplings, and sensor mappings.',
    example: 'A 2×2 matrix has 2 rows and 2 columns, transforming a 2-element state vector into 2 rate derivatives.',
  },
  {
    id: 'transpose',
    label: 'transpose',
    tex: '^T',
    meaning:
      'The linear algebra operation of converting rows into columns. A superscript $T$ on a row $[q, v]^T$ means write the list vertically as a column vector.',
    example: '$[q, v]^T$ represents $q$ stacked vertically above $v$.',
    spoken: 'transpose',
  },
  {
    id: 'subscript',
    label: 'subscript',
    meaning: 'Small text placed below and to the right of a mathematical symbol to label which specific parameter or operating point is referenced.',
    example: '$z_0$ specifies altitude at the operating baseline; $K_p$ identifies proportional gain.',
  },
  {
    id: 'superscript',
    label: 'superscript',
    meaning:
      'Small text placed above and to the right of a mathematical symbol. While numbers represent powers (like $\\text{s}^2$), a capital $T$ denotes matrix transpose.',
    example: '$\\text{m/s}^2$ means metres per second squared; $[q, v]^T$ denotes transpose.',
  },
  {
    id: 'A',
    label: 'state matrix',
    tex: 'A',
    meaning:
      'The system dynamics matrix: defines how the current state variables naturally govern their own rates of change through internal physics (such as springs and aerodynamic drag).',
    example: 'Row 2 of $A$ is $[-k/m, -b/m]$, mapping position and velocity into physical acceleration.',
    spoken: 'A',
  },
  {
    id: 'B',
    label: 'input matrix',
    tex: 'B',
    meaning: 'The control input matrix: describes how commanded motor thrust deviations ($u$) directly accelerate the vehicle.',
    example: '$B = [0, 1/m]^T$: commanded thrust changes acceleration ($\dot v$), not position directly.',
    spoken: 'B',
  },
  {
    id: 'E',
    label: 'disturbance matrix',
    tex: 'E',
    meaning: 'The disturbance coupling matrix: describes how outside environmental forces ($d$) accelerate the aircraft.',
    example: '$E = [0, 1/m]^T$: an external disturbance accelerates mass identically to rotor thrust.',
    spoken: 'E',
  },
  {
    id: 'C',
    label: 'output matrix',
    tex: 'C',
    meaning: 'The sensor output matrix: specifies which linear combination of state variables is observed and reported by ideal sensors.',
    example: '$C = [1, 0]$: the altimeter directly reports altitude deviation $q$, without measuring velocity.',
    spoken: 'C',
  },
  {
    id: 'Dmat',
    label: 'direct-feedthrough matrix',
    tex: 'D',
    meaning:
      'The direct-feedthrough matrix: specifies whether motor inputs immediately alter sensor readings with zero time delay. For physical flight vehicles, $D = 0$ because thrust must accelerate mass before position can move.',
    example: '$D = 0$: commanding motor thrust does not instantly alter the altimeter reading.',
    spoken: 'D',
  },
  {
    id: 'y',
    label: 'output',
    tex: 'y',
    meaning: 'The ideal sensor output: the true physical quantity the altimeter would measure in the absence of electronic noise ($y = C x = q$).',
    units: 'm',
    example: 'If $q = 0.3\\text{ m}$, ideal output is $y = 0.3\\text{ m}$, and displayed altitude is $z_0 + y = 2.3\\text{ m}$.',
    highlight: 'signal:z',
    spoken: 'y',
  },
  {
    id: 'ym',
    label: 'measured output',
    tex: 'y_m',
    meaning:
      'The actual measured sensor signal delivered to the flight computer, combining true physical displacement $y$ with sensor noise $n$ ($y_m = y + n$).',
    units: 'm',
    example: 'True altitude $y = 0.30\\text{ m}$ with noise $n = -0.01\\text{ m}$ produces a measurement $y_m = 0.29\\text{ m}$.',
    highlight: 'signal:ym',
    spoken: 'y m',
  },
  {
    id: 'n',
    label: 'sensor noise',
    tex: 'n',
    meaning:
      'Random measurement noise caused by electrical sensor interference or air turbulence. Flight computers must filter noise to prevent motor jitter.',
    units: 'm',
    example: 'Noise with standard deviation $\\sigma = 0.01\\text{ m}$ causes altimeter readings to fluctuate within $\\pm 2\\text{ cm}$.',
    highlight: 'signal:ym',
    spoken: 'n',
  },
  {
    id: 'rq',
    label: 'target deviation',
    tex: 'r_q',
    meaning: 'The commanded target altitude expressed relative to the operating hover point ($r_q = r - z_0$).',
    units: 'm',
    example: 'Target altitude $r = 2.5\\text{ m}$ with hover point $z_0 = 2.0\\text{ m}$ gives target deviation $r_q = +0.5\\text{ m}$.',
    highlight: 'signal:r',
    spoken: 'r q',
  },
  {
    id: 'linearized',
    label: 'linearized',
    meaning:
      'A mathematical model where all equations are simplified so every term is a constant multiplied by a single variable (no nonlinear powers, square roots, or switches). Linear models allow powerful mathematical analysis and control design.',
    example: '$\\dot v = -(k/m) q - (b/m) v + (1/m) u$ is linear near hover.',
  },
  {
    id: 'euler',
    label: 'Euler step',
    meaning:
      'A simple numerical integration technique that estimates future state by adding rate multiplied by time step: $x_{next} \\approx x + \\dot x\\,\\Delta t$. (The simulator uses high-accuracy RK4 integration with the same rate equations).',
    example: 'With velocity $v = 1\\text{ m/s}$ and $\\Delta t = 0.01\\text{ s}$, altitude climbs by approximately $\\Delta z \\approx 0.01\\text{ m}$.',
  },

  // ── Controllers ───────────────────────────────────────────────────
  {
    id: 'clamp',
    label: 'clamp',
    meaning:
      'A critical safety function in flight code that constrains commanded signals to within allowed hardware boundaries: $T = \\operatorname{clamp}(T_{cmd}, 0, T_{max})$.',
    example: '$\\operatorname{clamp}(30, 0, 25) = 25\\text{ N}$; $\\operatorname{clamp}(-3, 0, 25) = 0\\text{ N}$.',
  },
  {
    id: 'saturation',
    label: 'saturation',
    meaning:
      'The physical condition where the flight controller requests more thrust than the propulsion system can physically produce (or less than zero), causing motor output to freeze at a hard limit.',
    example: 'Requesting 30 N when motors cap out at 25 N saturates the actuator at high throttle.',
    highlight: 'signal:Tcmd',
  },
  {
    id: 'bang-bang',
    label: 'bang-bang control',
    meaning:
      'A binary control algorithm that toggles motors abruptly between full high throttle and low throttle depending on whether the vehicle is below or above target.',
    example: 'Thrust switches to $T_0 + 3\\text{ N}$ when below target and $T_0 - 3\\text{ N}$ when above target.',
  },
  {
    id: 'h',
    label: 'hysteresis half-band',
    tex: 'h',
    meaning:
      'Hysteresis half-band: the tolerance deadband around the target altitude within which a bang-bang controller keeps its existing state rather than switching.',
    units: 'm',
    example: 'With $h = 0.05\\text{ m}$, the controller switches to high only when $e > 0.05\\text{ m}$, and low only when $e < -0.05\\text{ m}$.',
    spoken: 'h',
  },
  {
    id: 'hysteresis',
    label: 'hysteresis',
    meaning:
      'Memory-based switching logic where flipping states requires crossing a threshold band. In bang-bang control, hysteresis prevents high-frequency motor chattering.',
    example: 'A home thermostat that switches heating on at 19 °C but waits until 21 °C to turn it off.',
  },
  {
    id: 'Kp',
    label: 'proportional gain',
    tex: 'K_p',
    meaning:
      'Proportional gain: the controller gain specifying how many Newtons of corrective thrust to command per metre of tracking error ($u_P = K_p e$). It acts like an artificial software spring pulling the aircraft toward target.',
    units: 'N/m',
    example: '$K_p = 4\\text{ N/m}$: an altitude sag of $0.25\\text{ m}$ generates $1\\text{ N}$ of extra upward thrust.',
    highlight: 'signal:uP',
    spoken: 'K p',
  },
  {
    id: 'Ki',
    label: 'integral gain',
    tex: 'K_i',
    meaning:
      'Integral gain: specifies how many Newtons of corrective thrust to generate per metre-second of accumulated error ($u_I = K_i I$). It eliminates steady-state load offsets.',
    units: 'N/(m·s)',
    example: '$K_i = 3\\text{ N/(m}\\cdot\\text{s)}$: an accumulated error of $0.5\\text{ m}\\cdot\\text{s}$ produces $1.5\\text{ N}$ of holding thrust.',
    highlight: 'signal:uI',
    spoken: 'K i',
  },
  {
    id: 'Kd',
    label: 'derivative gain',
    tex: 'K_d',
    meaning:
      'Derivative gain: specifies how many Newtons of braking force to apply per m/s of vehicle velocity ($u_D = -K_d v_{est}$). It acts like artificial software damping to suppress overshoot and oscillations.',
    units: 'N·s/m',
    example: '$K_d = 7\\text{ N}\\cdot\\text{s/m}$: climbing at $0.5\\text{ m/s}$ generates $3.5\\text{ N}$ of downward braking thrust.',
    highlight: 'signal:uD',
    spoken: 'K d',
  },
  {
    id: 'I',
    label: 'accumulated error',
    tex: 'I',
    meaning:
      'The accumulated error integral: the running sum of tracking error multiplied by time ($I_{next} = I + e\\,\\Delta t$), storing a memory of past deviations.',
    units: 'm·s',
    example: 'An altitude sag of $e = 0.2\\text{ m}$ lasting for $0.5\\text{ s}$ adds $0.1\\text{ m}\\cdot\\text{s}$ to $I$.',
    highlight: 'signal:uI',
    spoken: 'I',
  },
  {
    id: 'uP',
    label: 'P contribution',
    tex: 'u_P',
    meaning:
      'The proportional component of the feedback command: $u_P = K_p e$, providing corrective stiffness that pulls the vehicle toward the target.',
    units: 'N',
    example: 'With $K_p = 16\\text{ N/m}$ and error $e = 0.1\\text{ m}$, proportional thrust is $u_P = 1.6\\text{ N}$.',
    highlight: 'signal:uP',
    spoken: 'u P',
  },
  {
    id: 'uI',
    label: 'I contribution',
    tex: 'u_I',
    meaning:
      'The integral component of the feedback command: $u_I = K_i I$, supplying the persistent holding force needed to support unmodeled loads with zero steady-state error.',
    units: 'N',
    example: '$K_i = 3\\text{ N/(m}\\cdot\\text{s)}$ and $I = 0.33\\text{ m}\\cdot\\text{s}$ produces $u_I = 1.0\\text{ N}$, perfectly balancing a 1 N downward load.',
    highlight: 'signal:uI',
    spoken: 'u I',
  },
  {
    id: 'uD',
    label: 'D contribution',
    tex: 'u_D',
    meaning:
      'The derivative component of the feedback command: $u_D = -K_d v_{est}$, applying braking thrust against velocity to dampen oscillations and prevent overshoot.',
    units: 'N',
    example: 'Climbing at $0.2\\text{ m/s}$ with $K_d = 6\\text{ N}\\cdot\\text{s/m}$ commands a braking force of $u_D = -1.2\\text{ N}$.',
    highlight: 'signal:uD',
    spoken: 'u D',
  },
  {
    id: 'vest',
    label: 'velocity estimate',
    tex: 'v_{est}',
    meaning:
      'Estimated vertical velocity: calculated by differentiating successive altitude readings ($\Delta y_m / \Delta t$) and passing the result through a low-pass filter to reject sensor noise.',
    units: 'm/s',
    example: 'Altimeter samples of 2.000 m and 2.005 m separated by $0.005\\text{ s}$ yield a raw velocity of $1\\text{ m/s}$ before filtering.',
    highlight: 'signal:v',
    spoken: 'v estimate',
  },
  {
    id: 'filter',
    label: 'low-pass filter',
    tex: '\\tau',
    meaning:
      'A digital first-order low-pass filter that smooths out high-frequency sensor noise. The filter time constant $\\tau$ balances noise reduction against phase lag.',
    units: 's',
    example: 'A filter with $\\tau = 0.05\\text{ s}$ removes high-frequency altimeter jitter from $v_{est}$, preventing motor vibration.',
    spoken: 'tau',
  },
  {
    id: 'derivative-on-measurement',
    label: 'derivative on measurement',
    meaning:
      'Computing the derivative control term using measured vehicle velocity ($-K_d v_{est}$) instead of error rate ($K_d \\dot e$). This eliminates violent motor spikes (derivative kick) during sudden step target changes.',
    example: 'When target altitude steps from 2 m to 2.5 m, the target jumps instantly, but measured altitude cannot, so $u_D$ remains smooth and well-behaved.',
  },
  {
    id: 'windup',
    label: 'integrator windup',
    meaning:
      'The dangerous accumulation of the error integral while actuators are saturated at maximum limits. When the vehicle finally reaches target, the bloated integral holds motors pinned at 100%, causing severe overshoot.',
    example: 'Requesting 40 N for 5 seconds when motors cap at 25 N inflates $I$ to a huge value, delaying recovery when the target drops.',
  },
  {
    id: 'anti-windup',
    label: 'anti-windup',
    meaning:
      'A protection mechanism in flight software that freezes the integrator whenever actuators are saturated and error would drive them deeper into saturation, while allowing immediate unwinding.',
    example: 'When thrust is saturated at $T_{max}$ and the drone is still below target, anti-windup clamps $I$ to prevent overshoot.',
  },
  {
    id: 'residual-error',
    label: 'residual (steady-state) error',
    meaning:
      'The persistent tracking error that remains after the vehicle has fully settled. Pure proportional control always leaves residual error under an external load.',
    units: 'm',
    example: 'Proportional control with $K_p = 4\\text{ N/m}$ under a 1 N load leaves a steady sag of $e_{steady} \\approx 0.167\\text{ m}$.',
    highlight: 'signal:e',
  },
  {
    id: 'control-effort',
    label: 'control effort',
    meaning:
      'A measure of how aggressively the flight controller demands torque and thrust from the actuators. Excessive control effort leads to motor heating, battery drain, and actuator wear.',
    example: 'An overly aggressive $K_p$ commands massive, spiky thrust requests at every target change.',
    highlight: 'signal:Tcmd',
  },

  // ── Response shape ────────────────────────────────────────────────
  {
    id: 'zeta',
    label: 'damping ratio',
    tex: '\\zeta',
    meaning:
      'Damping ratio (dimensionless, Greek zeta $\\zeta$): characterizes how quickly oscillations decay relative to the critically damped boundary.',
    units: 'unitless',
    example: '$\\zeta = 0.25$ produces ringing oscillations; $\\zeta = 1.0$ is critically damped; $\\zeta = 2.0$ creeps sluggishly without overshooting.',
    spoken: 'zeta',
  },
  {
    id: 'wn',
    label: 'natural angular frequency',
    tex: '\\omega_n',
    meaning:
      'Natural angular frequency (Greek omega $\\omega_n$): the frequency at which the vehicle would naturally oscillate if there were zero damping (measured in radians per second).',
    units: 'rad/s',
    example: 'With $k = 4\\text{ N/m}$ and $m = 1\\text{ kg}$, natural frequency is $\\omega_n = \\sqrt{4/1} = 2\\text{ rad/s}$ (oscillation period $\\approx 3.1\\text{ s}$).',
    spoken: 'omega n',
  },
  {
    id: 'underdamped',
    label: 'underdamped',
    meaning:
      '$0 < \\zeta < 1$: the vehicle responds quickly but overshoots the target altitude, ringing with decaying sinusoidal oscillations before settling.',
    example: 'Our physical release experiment with $b = 1\\text{ N}\\cdot\\text{s/m}$ ($\zeta = 0.25$).',
  },
  {
    id: 'critically-damped',
    label: 'critically damped',
    meaning:
      '$\\zeta = 1$: the dynamic boundary between oscillating and non-oscillating motion, returning the aircraft to equilibrium as rapidly as possible without overshooting.',
    example: 'The physical release experiment with $b = 4\\text{ N}\\cdot\\text{s/m}$ ($\zeta = 1.0$).',
  },
  {
    id: 'overdamped',
    label: 'overdamped',
    meaning:
      '$\\zeta > 1$: excessive damping prevents all oscillation and overshoot, but causes the aircraft to creep sluggishly into position.',
    example: 'The physical release experiment with $b = 8\\text{ N}\\cdot\\text{s/m}$ ($\zeta = 2.0$).',
  },
  {
    id: 'rise-time',
    label: 'rise time',
    meaning:
      'The time required for vehicle altitude to climb from 10% to 90% of a commanded step change in target altitude, measuring initial responsiveness.',
    units: 's',
    example: 'For a 0.5 m step from 2.0 m to 2.5 m, rise time is the time taken to travel from 2.05 m to 2.45 m.',
  },
  {
    id: 'overshoot',
    label: 'overshoot',
    meaning:
      'The peak distance the aircraft surges past its target altitude, expressed as a percentage of the commanded step size. Flight controllers must minimize overshoot to avoid ceiling collisions.',
    units: '% of step',
    example: 'Climbing toward 2.5 m and peaking at 2.55 m on a 0.5 m step represents an overshoot of $\\frac{0.05}{0.50} = 10\\%$.',
  },
  {
    id: 'settling-time',
    label: 'settling time',
    meaning:
      'The elapsed time from a command or disturbance until vehicle altitude enters and permanently remains within a tight tolerance band (typically $\\pm 5\\text{ cm}$) around the target.',
    units: 's',
    example: 'Entering within $\\pm 0.05\\text{ m}$ of 2.5 m at $t = 2.8\\text{ s}$ after a step command at $t = 1.0\\text{ s}$ yields a settling time of $1.8\\text{ s}$.',
  },
  {
    id: 'iae',
    label: 'accumulated error magnitude (IAE)',
    meaning:
      'Integrated Absolute Error (IAE): the total accumulated area of $|error|$ over an entire flight run ($\\int |e|\\,dt$), penalizing both large transients and lingering offsets.',
    units: 'm·s',
    example: 'A persistent error of $0.1\\text{ m}$ lasting for $5\\text{ s}$ contributes $0.5\\text{ m}\\cdot\\text{s}$ to the total IAE score.',
  },
];

/** Entries that read better as words than as their symbol when used without custom text. */
export const LABEL_FIRST = new Set(['reference', 'disturbance', 'error', 'edot', 'state', 'weight', 'feedforward', 'filter', 'transpose', 'xdot']);

export const GLOSSARY: Record<string, GlossaryEntry> = Object.fromEntries(entries.map((e) => [e.id, e]));
export const GLOSSARY_LIST = entries;

export function getTerm(id: string): GlossaryEntry {
  const t = GLOSSARY[id];
  if (!t) throw new Error(`Unknown glossary term "${id}"`);
  return t;
}
